import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import config from '../../config';
import './NatureEventDetail.css';
import { trackEvent } from '../../services/trackingService';

const NatureEventDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [bookingLoading, setBookingLoading] = useState(false);
    const [selectedTier, setSelectedTier] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [isExpanded, setIsExpanded] = useState(false);
    const [quantityError, setQuantityError] = useState('');
    const [expandedTiers, setExpandedTiers] = useState({});

    const toggleTierDescription = (tierId, e) => {
        e.stopPropagation();
        setExpandedTiers(prev => ({
            ...prev,
            [tierId]: !prev[tierId]
        }));
    };
    const [showPromoModal, setShowPromoModal] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoStep, setPromoStep] = useState('input'); // 'input' or 'message'
    const hasTrackedView = useRef(false);
    const hasAttemptedAutoOpen = useRef(false);
    const hostData = eventData?.host?.host || eventData?.host;

    // Device detection and app redirect function
    const handleOpenInApp = useCallback((redirectIfNotFound = false) => {
        // Try to open the app via deep link
        const isProductionHost = window.location.hostname === 'share.outr-connect.com' || window.location.hostname === 'sharing.outr-connect.com';
        const primaryHost = isProductionHost ? window.location.host : 'share.outr-connect.com';
        const fallbackHost = primaryHost === 'sharing.outr-connect.com' ? 'share.outr-connect.com' : 'sharing.outr-connect.com';

        window.location.href = `outrapp://${primaryHost}/share?pageName=eventDetail&eventId=${id}`;

        setTimeout(() => {
            if (document.visibilityState === 'visible') {
                window.location.href = `outrapp://${fallbackHost}/share?pageName=eventDetail&eventId=${id}`;
            }
        }, 500);

        if (redirectIfNotFound) {
            let appOpened = false;

            const handleVisibilityChange = () => {
                if (document.hidden || document.visibilityState === 'hidden') {
                    appOpened = true;
                }
            };

            const handleBlur = () => {
                appOpened = true;
            };

            window.addEventListener('visibilitychange', handleVisibilityChange);
            window.addEventListener('pagehide', handleVisibilityChange);
            window.addEventListener('blur', handleBlur);

            setTimeout(() => {
                window.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('pagehide', handleVisibilityChange);
                window.removeEventListener('blur', handleBlur);

                if (!appOpened && document.visibilityState === 'visible') {
                    const ua = navigator.userAgent || '';
                    const isAndroid = /android/i.test(ua);
                    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

                    if (isAndroid) {
                        window.location.href = 'https://play.google.com/store/apps/details?id=com.app.outr';
                    } else if (isIOS) {
                        window.location.href = 'https://apps.apple.com/us/app/outr/id6748447370';
                    } else {
                        alert('Please visit the App Store or Play Store on your mobile device to download the app.');
                    }
                }
            }, 2500);
        }
    }, [id]);

    // Track VIEW exactly once when eventData is loaded
    useEffect(() => {
        if (eventData && eventData._id && !hasTrackedView.current) {
            trackEvent({
                eventId: eventData._id,
                hostId: hostData?._id || 'unknown',
                actionType: 'VIEW',
                metadata: { source: "deeplink", platform: "web" }
            });
            hasTrackedView.current = true;
        }
    }, [eventData]);

    // Auto-open app on load if eventData is ready (no store redirection on failure)
    useEffect(() => {
        if (eventData && !hasAttemptedAutoOpen.current) {
            hasAttemptedAutoOpen.current = true;
            handleOpenInApp(false);
        }
    }, [eventData, handleOpenInApp]);

    // Update Document Title and Dynamic Open Graph/SEO Meta Tags
    useEffect(() => {
        if (eventData) {
            // Update page title
            document.title = `${eventData.title} | Outr`;

            // Helper function to update or create meta tags in <head>
            const updateMetaTag = (property, content, isName = false) => {
                if (!content) return;
                const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
                let element = document.querySelector(selector);
                if (!element) {
                    element = document.createElement('meta');
                    element.setAttribute(isName ? 'name' : 'property', property);
                    document.head.appendChild(element);
                }
                element.setAttribute('content', content);
            };

            // Parse description (strip HTML tags) and truncate
            const cleanDescription = eventData.description 
                ? eventData.description.replace(/<[^>]*>/g, '').substring(0, 160) 
                : 'Join us for this event on Outr!';

            // Set Open Graph tags
            updateMetaTag('og:title', eventData.title);
            updateMetaTag('og:description', cleanDescription);
            updateMetaTag('og:image', eventData.image || 'https://share.outr-connect.com/logo.png');
            updateMetaTag('og:url', window.location.href);
            updateMetaTag('og:type', 'website');

            // Set Twitter tags
            updateMetaTag('twitter:title', eventData.title);
            updateMetaTag('twitter:description', cleanDescription);
            updateMetaTag('twitter:image', eventData.image || 'https://share.outr-connect.com/logo.png');

            // Set standard description for SEO
            updateMetaTag('description', cleanDescription, true);
        }
    }, [eventData]);

    // Set default tier to the first available bookable tier
    useEffect(() => {
        if (eventData?.tiers && eventData.tiers.length > 0) {
            const visibleTiers = eventData.tiers.filter(t => 
                t.publicView !== 2 && t.publicView !== '2' && t.publicView !== 0 && t.publicView !== '0' && t.publicView !== false
            );
            const now = new Date();
            const firstAvailable = visibleTiers.find(t => 
                (Number(t.availableQty) > 0 || (Number(t.capacity || 0) > Number(t.soldQty || 0))) &&
                (!t.salesEndDate || new Date(t.salesEndDate) >= now)
            );
            if (firstAvailable) {
                setSelectedTier(firstAvailable);
            }
        }
    }, [eventData]);

    const handleTierSelect = (tier) => {
        if (tier.availableQty > 0) {
            setSelectedTier(tier);
            setQuantity(1);
        }
    };

    const handleQuantityChange = (delta) => {
        if (!selectedTier) return;
        const newQty = quantity + delta;
        if (newQty > 5) {
            setQuantityError('You can select a maximum of 5 tickets.');
            return;
        }
        if (newQty < 1) return;
        if (newQty <= selectedTier.availableQty) {
            setQuantity(newQty);
            setQuantityError('');
        }
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    const handleEmailChange = (e) => {
        const val = e.target.value;
        setEmail(val);
        if (!val) {
            setEmailError('Email is required');
        } else if (!validateEmail(val)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    const handleBuyTickets = async () => {
        if (!selectedTier) {
            alert("Please select a ticket tier.");
            return;
        }

        if (!email || emailError) {
            setEmailError('Email is required');
            // alert("Please enter a valid email address.");
            return;
        }

        setBookingLoading(true);

        const payload = {
            eventId: eventData._id,
            tierId: selectedTier._id,
            qty: quantity,
            guestEmail: email
        };

        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/user/out-off-ticket-booking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "ngrok-skip-browser-warning": "any-value"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                const checkoutUrl = data.checkoutUrl || data.data?.checkoutUrl;
                if (checkoutUrl) {
                    window.location.href = checkoutUrl;
                } else {
                    alert("Booking successful! Check your email for details.");
                }
            } else {
                alert(data.message || "Booking failed. Please try again.");
            }
        } catch (error) {
            console.error("Booking error:", error);
            alert("An error occurred. Please check your connection.");
        } finally {
            setBookingLoading(false);
        }
    };

    const handleBuyButtonClick = () => {
        const visibleTiers = (eventData?.tiers || []).filter(t => 
            t.publicView !== 2 && t.publicView !== '2' && t.publicView !== 0 && t.publicView !== '0' && t.publicView !== false
        );
        const hasAvailableTickets = visibleTiers.some(tier => tier.availableQty > 0);
        if (visibleTiers.length === 0 || !hasAvailableTickets) {
            if (eventData?.bookingUrl) {
                window.location.href = eventData.bookingUrl;
            } else {
                alert("This event currently has no tickets available.");
            }
        } else {
            setShowModal(true);
        }
    };

    useEffect(() => {
        const fetchEventData = async () => {
            try {
                const response = await fetch(`${config.API_BASE_URL}/api/v1/user/event-preview?id=${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        "ngrok-skip-browser-warning": "any-value"
                    }
                });
                const data = await response.json();

                if (data?.data) {
                    setEventData(data.data);
                }
            } catch (error) {
                console.error("Error fetching event data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchEventData();
        }
    }, [id]);

    const handleSaveEvent = (e) => {
        if (e) e.preventDefault();
        if (eventData && eventData._id) {
            trackEvent({
                eventId: eventData._id,
                hostId: hostData?._id || 'unknown',
                actionType: 'SAVE',
                metadata: { source: "deeplink", platform: "web" }
            });
        }
        handleOpenInApp(true);
    };

    // Function to directly redirect to app stores
    const handleDownloadApp = (e, store) => {
        if (e) e.preventDefault();
        const ua = navigator.userAgent || '';
        const isAndroid = /android/i.test(ua);
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (store === 'play' || (isAndroid && !store)) {
            window.location.href = 'https://play.google.com/store/apps/details?id=com.app.outr';
        } else if (store === 'appstore' || (isIOS && !store)) {
            window.location.href = 'https://apps.apple.com/us/app/outr/id6748447370';
        } else {
            alert('Please visit the App Store or Play Store on your mobile device to download the app.');
        }
    };

    if (loading) {
        return <div className="nature-event-container">Loading...</div>;
    }

    if (!eventData) {
        return (
            <div className="nature-empty-state">
                <div className="empty-state-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                </div>
                <h2 className="empty-state-title">Event Not Found</h2>
                <p className="empty-state-text">
                    This event is no longer available or the link may be incorrect.
                </p>
                {/* <button
                    className="btn-primary empty-state-btn"
                    onClick={() => navigate('/')}
                >
                    Go to Home Screen
                </button> */}


            </div>
        );
    }

    // Format Date
    const eventDate = new Date(eventData.date);
    const dateString = eventDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).toUpperCase();
    const timeString = eventDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' }).toUpperCase();

    const getFormattedPrice = () => {
        if (!eventData) return '';
        const now = new Date();
        const visibleTiers = (eventData.tiers || []).filter(t => 
            t.publicView !== 2 && t.publicView !== '2' && t.publicView !== 0 && t.publicView !== '0' && t.publicView !== false
        );
        const bookableTiers = visibleTiers.filter(t => {
            const hasQty = (Number(t.availableQty) > 0) || (Number(t.capacity || 0) > Number(t.soldQty || 0));
            const notExpired = !t.salesEndDate || new Date(t.salesEndDate) >= now;
            return hasQty && notExpired;
        });

        // Determine effective price: prefer lowest bookable tier price, then eventData.price, then lowest visible tier
        let priceVal = null;
        if (bookableTiers.length > 0) {
            const prices = bookableTiers.map(t => Number(t.price) || 0);
            priceVal = Math.min(...prices);
        } else if (eventData.price !== undefined && eventData.price !== null) {
            priceVal = Number(eventData.price) || 0;
        } else if (visibleTiers.length > 0) {
            const prices = visibleTiers.map(t => Number(t.price) || 0);
            priceVal = Math.min(...prices);
        } else {
            priceVal = 0;
        }

        if (priceVal === 0) return 'Free';
        const isTiered = eventData.hasMultipleTiers || visibleTiers.length > 1;
        return isTiered ? `From £${priceVal.toFixed(2)}` : `£${priceVal.toFixed(2)}`;
    };

    return (
        <div className="nature-event-container">
            <div className="nature-event-content">
                {/* Header Section */}
                <header className="nature-header">
                    <div className="nature-logo-circle">
                        <img
                            src="/image/outr-ticket-logo.png"
                            alt="Outr Logo"
                            className="nature-logo-img"
                        />
                    </div>
                    <button className="open-app-btn" onClick={() => handleOpenInApp(true)}>Open in App</button>
                </header>

                {/* Hero Image Section */}

                <div className="nature-hero-section">
                    <img
                        src={eventData.image || "/image/image-1.png"}
                        alt={eventData.title}
                        className="nature-hero-img"
                    />
                    {/* <div className="hero-overlay">
                        <div className="hero-subtitle">
                            <span>{eventData.category?.[0]?.name || "Event"}</span>
                            <span>{dateString}</span>
                        </div>
                    </div> */}
                </div>

                {/* Event Details Section */}
                <div className="nature-details-section">
                    <h2 className="event-title">{eventData.title}</h2>
                    <div
                        className="event-description"
                        dangerouslySetInnerHTML={{
                            __html: eventData.description && eventData.description.length > 200 && !isExpanded
                                ? `${(() => {
                                    let truncated = eventData.description.substring(0, 200);
                                    const lastOpenTag = truncated.lastIndexOf('<');
                                    const lastCloseTag = truncated.lastIndexOf('>');
                                    if (lastOpenTag > lastCloseTag) {
                                        return truncated.substring(0, lastOpenTag);
                                    }
                                    return truncated;
                                })()}...`
                                : eventData.description
                        }}
                    />
                    {eventData.description && eventData.description.length > 200 && (
                        <button
                            className="description-toggle-btn"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? "Short Description" : "Full Description"}
                        </button>
                    )}

                    {((eventData.category && eventData.category.length > 0) || (eventData.tags && eventData.tags.length > 0)) && (
                        <div className="nature-tags-container">
                            {eventData.category?.map(cat => (
                                <span key={cat._id} className="nature-tag">{cat.name}</span>
                            ))}
                            {eventData.tags?.map((tag, index) => (
                                <span key={index} className="nature-tag">{tag}</span>
                            ))}
                        </div>
                    )}
                    <div className="hosted-by-section">
                        <h3 className="section-title">Hosted by</h3>
                        <div className="host-info">
                            <img
                                src={hostData?.profileImage || "/image/image-1.png"}
                                alt={hostData?.name}
                                className="host-avatar"
                            />
                            <span className="host-name">{hostData?.name || "Mac Jonathan"}</span>
                        </div>
                    </div>
                    <div className="price-row">
                        <span className="price-label">Ticket Price</span>
                        <span className="price-value">{getFormattedPrice()}</span>
                    </div>

                    <div className="info-item">
                        <div className="info-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </div>
                        <div className="info-text">
                            {dateString} • {timeString}
                        </div>
                    </div>

                    <div className="info-item">
                        <div className="info-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                        </div>
                        <div className="info-text">{eventData.location?.address || "Location TBD"}</div>
                    </div>
                </div>

                <hr className="divider" />

                {/* Action Buttons */}
                <div className="action-buttons">
                    <button className="btn-secondary" onClick={handleSaveEvent}>Save this event</button>
                    <button className="btn-primary" onClick={handleBuyButtonClick}>Buy Tickets</button>
                </div>

                {/* Hosted By Section */}


                {/* Map Section */}
                <div className="map-section">
                    <iframe
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(eventData.location?.address || "Camley Street Natural Park")}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                        width="100%"
                        height="200"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Event Location"
                        className="map-iframe"
                    ></iframe>
                </div>

                {/* QR Code Section */}
                <div className="qr-section">
                    <div className="qr-code-wrapper">
                        <QRCodeSVG
                            value={`https://${window.location.host}/share?pageName=eventDetail&eventId=${id}`}
                            size={150}
                            level="H"
                            includeMargin={false}
                        />
                    </div>
                    {/* <p className="qr-text">SCAN TO JOIN</p> */}
                </div>

                {/* Footer / App Stores */}
                <div className="nature-footer">
                    <div className="store-buttons">
                        <a href="https://play.google.com/store/apps/details?id=com.app.outr" onClick={(e) => handleDownloadApp(e, 'play')}>
                            <img src="/play-store.svg" alt="Get on Google Play" className="store-badge" />
                        </a>
                        <a href="https://apps.apple.com/us/app/outr/id6748447370" onClick={(e) => handleDownloadApp(e, 'appstore')}>
                            <img src="/app-store.svg" alt="Download on App Store" className="store-badge" />
                        </a>
                    </div>
                </div>

                {/* Booking Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3 className="modal-title">Book your tickets</h3>
                                <button className="close-modal" onClick={() => setShowModal(false)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                {eventData.tiers && eventData.tiers.length > 0 && (
                                    <div className="tiers-section">
                                        <h4 className="modal-section-title">Select Ticket</h4>
                                        <div className="tiers-list">
                                            {eventData.tiers
                                                .filter(tier => tier.publicView !== 2 && tier.publicView !== '2' && tier.publicView !== 0 && tier.publicView !== '0' && tier.publicView !== false)
                                                .map((tier) => (
                                                <div
                                                    key={tier._id}
                                                    className={`nature-tier-card ${selectedTier?._id === tier._id ? 'selected' : ''} ${tier.availableQty === 0 ? 'sold-out' : ''}`}
                                                    onClick={() => handleTierSelect(tier)}
                                                >
                                                     <div className="tier-info">
                                                         <span className="tier-name">{tier.name || eventData.title}</span>
                                                         {/* <span className="tier-availability">{tier.availableQty > 0 ? `${tier.availableQty} left` : 'Sold Out'}</span> */}
                                                         {tier.availableQty === 0 && <span className="tier-sold-out-msg">No tickets available</span>}
                                                         {tier.description && (
                                                             <div className="tier-description">
                                                                 {tier.description.length > 80 && !expandedTiers[tier._id] ? (
                                                                     <>
                                                                         {tier.description.substring(0, 80)}...
                                                                         <button
                                                                             className="tier-read-more-btn"
                                                                             onClick={(e) => toggleTierDescription(tier._id, e)}
                                                                         >
                                                                             Read more
                                                                         </button>
                                                                     </>
                                                                 ) : (
                                                                     <>
                                                                         {tier.description}
                                                                         {tier.description.length > 80 && (
                                                                             <button
                                                                                 className="tier-read-more-btn"
                                                                                 onClick={(e) => toggleTierDescription(tier._id, e)}
                                                                             >
                                                                                 Read less
                                                                             </button>
                                                                         )}
                                                                     </>
                                                                 )}
                                                             </div>
                                                         )}
                                                     </div>
                                                    <div className="tier-price">
                                                        {tier.price === 0 ? 'Free' : `£${tier.price}`}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedTier && (
                                    <div className="quantity-section">
                                        <div className="quantity-control-row">
                                            <div className="quantity-label-group">
                                                <span className="quantity-label">Quantity</span>
                                                {/* <span className="quantity-available">
                                                    {selectedTier.availableQty} tickets left
                                                </span> */}
                                            </div>
                                            <div className="quantity-controls">
                                                <button
                                                    className="qty-btn"
                                                    onClick={() => handleQuantityChange(-1)}
                                                    disabled={quantity <= 1}
                                                >
                                                    -
                                                </button>
                                                <span className="qty-value">{quantity}</span>
                                                <button
                                                    className="qty-btn"
                                                    onClick={() => handleQuantityChange(1)}
                                                    disabled={quantity >= selectedTier.availableQty || quantity >= 5}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                        {quantityError && <div className="quantity-error-message">{quantityError}</div>}

                                        <div className="total-row">
                                            <span className="total-label">Total Price</span>
                                            <span className="total-value">
                                                {selectedTier.price === 0 ? 'Free' : `£${(selectedTier.price * quantity).toFixed(2)}`}
                                            </span>
                                        </div>


                                    </div>
                                )}
                                <div className="promo-code-section">
                                    <button className="promo-code-link" onClick={() => {
                                        setPromoStep('message');
                                        setPromoCode('');
                                        setShowPromoModal(true);
                                    }}>Apply Promo Code</button>
                                </div>
                                <div className="email-section">
                                    <label className="email-label">Your Email</label>
                                    <input
                                        type="email"
                                        className={`nature-input ${emailError ? 'error' : ''}`}
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder="Enter your email address"
                                    />
                                    {emailError && <span className="error-message">{emailError}</span>}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn-primary modal-buy-btn"
                                    onClick={handleBuyTickets}
                                    disabled={bookingLoading}
                                >
                                    {bookingLoading ? 'Processing...' : 'Confirm & Buy'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Promo Code Modal */}
                {showPromoModal && (
                    <div className="modal-overlay" onClick={() => setShowPromoModal(false)}>
                        <div className="modal-container promo-modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3 className="modal-title">Apply Promo Code</h3>
                                <button className="close-modal" onClick={() => setShowPromoModal(false)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body promo-modal-body">
                                {promoStep === 'input' ? (
                                    <div className="promo-input-step">
                                        <p className="promo-step-description">Enter your promo code below to apply it to your booking.</p>
                                        <div className="promo-input-group">
                                            <input
                                                type="text"
                                                className="nature-input promo-input"
                                                placeholder="Enter promo code"
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value)}
                                            />
                                            <button
                                                className="btn-primary apply-promo-btn"
                                                onClick={() => setPromoStep('message')}
                                                disabled={!promoCode.trim()}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="promo-message-step">
                                        <div className="promo-message-icon">
                                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                        </div>
                                        <p className="promo-message-text">
                                            Promo codes can only be applied via the Outr app. Open the app to apply code.
                                        </p>
                                        <button className="btn-primary promo-download-btn" onClick={() => handleOpenInApp(true)}>
                                            Open App
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NatureEventDetail;
